import React, { useEffect, useRef, useState } from "react"
import AboutWindow from "./AboutWindow"
import { useWindowGeometry, RESIZE_HANDLES } from "../hooks/useWindowGeometry"
import { APPS } from "../utils/appRegistry"
import { embedUrl } from "../hooks/useIsEmbedded"

/**
 * One window: chrome, its own box, and whatever is inside it.
 *
 * Each instance calls useWindowGeometry for itself, which is what lets there
 * be more than one. The hook was always per-window, it just had nowhere to
 * live but the shell.
 *
 * Contents come one of two ways. The window showing the current route renders
 * that route's page directly, as `children`: it is the real page, it costs
 * nothing extra, and it keeps the URL meaningful. Every other window loads its
 * route in an iframe, which buys the isolation that matters most here, since
 * half these apps hang keyboard handlers off `window` and two of them running
 * at once would otherwise drive the lander and the car with the same arrow key.
 */
const Window = ({
  title,
  route,
  surfaceRef,
  isShaded,
  onToggleShade,
  onClose,
  onFocus,
  isFocused,
  zIndex,
  embedded,
  initialBox,
  children,
}) => {
  const winRef = useRef(null)
  const { geometry, isDragging, begin } = useWindowGeometry(
    winRef,
    surfaceRef,
    initialBox
  )
  const [isMaximized, setIsMaximized] = useState(false)
  const [isAboutOpen, setIsAboutOpen] = useState(false)

  const app = APPS[route]

  /* A window that changes what it is showing should not keep the previous
     program's About box open over the new one. */
  useEffect(() => {
    setIsAboutOpen(false)
  }, [route])

  // A maximized or rolled-up window ignores its stored box: maximized fills
  // the surface, and shading has to collapse to the title bar's own height.
  const isFloating = geometry && !isMaximized
  const style = {
    zIndex,
    ...(isFloating
      ? {
          position: "absolute",
          left: geometry.x,
          top: geometry.y,
          width: geometry.w,
          height: isShaded ? "auto" : geometry.h,
          maxWidth: "none",
          alignSelf: "auto",
        }
      : null),
  }

  return (
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
    <section
      ref={winRef}
      style={style}
      onPointerDownCapture={onFocus}
      className={`win ${isShaded ? "is-shaded" : ""} ${
        isMaximized ? "is-maximized" : ""
      } ${isDragging ? "is-dragging" : ""} ${isFocused ? "is-focused" : ""}`}
    >
      {/* A drag surface has no keyboard equivalent, and the double-click only
          mirrors the Maximize button, which is a real button. So there is
          nothing here a keyboard user cannot reach. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="win-titlebar"
        onPointerDown={e => {
          // Let the control buttons handle their own clicks.
          if (e.target.closest(".win-controls")) return
          if (isMaximized) return
          begin("move", e)
        }}
        onDoubleClick={e => {
          if (e.target.closest(".win-controls")) return
          setIsMaximized(v => !v)
        }}
      >
        <span className="win-title">{title}</span>
        <div className="win-controls">
          {app && (
            <button
              type="button"
              className="win-btn win-btn-about"
              onClick={() => setIsAboutOpen(v => !v)}
              aria-expanded={isAboutOpen}
              aria-label={
                isAboutOpen
                  ? `Close About ${app.name}`
                  : `About ${app.name}, and its source`
              }
              title={isAboutOpen ? "Close About" : "About this app"}
            >
              <span className="glyph-about" aria-hidden="true">
                ?
              </span>
            </button>
          )}
          <button
            type="button"
            className="win-btn"
            onClick={onToggleShade}
            aria-label={isShaded ? "Restore window" : "Roll up window"}
            title={isShaded ? "Restore" : "Roll up"}
          >
            <span className="glyph-minimize" />
          </button>
          <button
            type="button"
            className="win-btn"
            onClick={() => setIsMaximized(v => !v)}
            aria-label={isMaximized ? "Restore down" : "Maximize"}
            title={isMaximized ? "Restore down" : "Maximize"}
          >
            <span className="glyph-maximize" />
          </button>
          <button
            type="button"
            className="win-btn win-btn-close"
            onClick={onClose}
            aria-label="Close window"
            title="Close"
          >
            <span className="glyph-close" />
          </button>
        </div>
      </div>

      <div className="win-body">
        {embedded ? (
          <iframe
            className="win-frame"
            src={embedUrl(route)}
            title={title}
            /* Same origin, so nothing here is sandboxed away from the site
               itself. The window is a view onto this site's own page. */
          />
        ) : (
          children
        )}
      </div>

      {/* Rolled up, there is nothing to sit on top of. */}
      {app && isAboutOpen && !isShaded && (
        <AboutWindow
          route={route}
          app={app}
          onClose={() => setIsAboutOpen(false)}
        />
      )}

      {/* Resize grips. Decorative: dragging is an enhancement, and the window
          is fully usable from the keyboard without them. */}
      {!isMaximized &&
        !isShaded &&
        RESIZE_HANDLES.map(edge => (
          <span
            key={edge}
            aria-hidden="true"
            className={`win-grip win-grip-${edge}`}
            onPointerDown={e => begin(edge, e)}
          />
        ))}
    </section>
  )
}

export default Window
